<template>
  <div>
    <loader :loading="loading || editAnnouncementUpdating"/>
    <div v-if="!loading">
      <page-title title="Editar Anúncio" moduleIdentifier="mercadoLivre">
        <div slot="header-right">
          <span class="badge" :class="product.status">{{ $t(`${product.status}.label`) }}</span>
          <button class="btn" :title="statusActionText" @click="changeStatus" v-if="canShowStatusAction">
            <i v-bind:class=statusActionIcon></i>
          </button>
        </div>
      </page-title>

      <div v-if="product && sourceProduct">
        <marketplace-announcement-edit-product ref="marketplaceProvideProduct" :sourceProduct="sourceProduct" :product="product"/>
      </div>

      <panel>
        <button class="btn btn--success" @click="save">{{ $t('save.label') }}</button>
        <button class="btn btn--success" @click="savePrice">{{ $t('save.price.label') }}</button>
      </panel>
    </div>
  </div>
</template>

<script>
  import api from '@/api'
  import { merge } from 'lodash'
  import { mapActions, mapGetters } from 'vuex'
  import { mapFields } from 'vuex-map-fields'
  import Loader from '@/components/template/Loader'
  import Panel from '@/components/template/Panel'
  import FormGroup from '@/components/template/FormGroup'
  import notifier from '@/notifier'
  import MarketplaceAnnouncementEditProduct from '@/components/marketplace/mercadoLivre/announcement/edit/Product'
  import PageTitle from '@/components/template/PageTitle'
  import ButtonActions from '@/components/template/ButtonActions'

  export default {
    provide: {
      maxDescriptionLengthDI: 50000,
      maxTitleLengthDI: 60
    },
    data: () => ({
      loading: true,
      product: null,
      toModule: null,
      sourceProduct: null
    }),
    props: {
      identifier: {
        type: String,
        required: true
      }
    },
    async created () {
      this.resetProduct()
    },
    methods: {
      ...mapActions('productsMarketplace', [
        'addItem',
        'clearProductsMarketplace'
      ]),
      ...mapActions('editAnnouncement', [
        'setAnnouncement',
        'update',
        'activate',
        'pause'
      ]),
      ...mapActions('errors', [
        'clearErrors'
      ]),
      async resetProduct () {
        this.product = this.getById(this.identifier)
        if (!this.product) {
          await this.addItem({type: 'marketplace', identifier: 'mercadoLivre', id: this.identifier})
          this.product = this.getById(this.identifier)
        }

        try {
          this.sourceProduct = await api.getProduct(this.product.source.type, this.product.source.identifier, this.product.productId)
        } catch (e) {
          notifier.error(`Produto não encontrado na fonte! Anúncio ${this.product.code}.`, 'Erro')
          return this.redirectToProductsSentReport()
        }

        if (this.product.variationsCombination !== null) {
          for (let i in Object.keys(this.product.variations)) {
            const variation = this.product.variations[i]
            variation.selected = true
          }

          for (let i in this.sourceProduct.variations) {
            const variationSource = Object.assign({}, this.sourceProduct.variations[i])

            const fields = {
              status: null,
              errors: [],
              selected: false,
              stock: {
                quantity: variationSource.quantity,
                reducer: this.toModuleDefinition.module.configs.product.stock.reducer
              },
              price: {
                type: this.toModuleDefinition.module.configs.product.price.type,
                cost: 0,
                auxiliary: 0,
                previous: variationSource.previousPrice,
                current: variationSource.currentPrice
              },
              attributes: []
            }

            merge(variationSource, fields)

            if (this.product.variations.find(variation => variation.sku === variationSource.sku) === undefined) {
              this.product.variations.push(variationSource)
            }
          }
        }

        setTimeout(() => (this.loading = false), 1500)
      },
      async save () {
        this.clearErrors()

        const formattedProduct = this.$refs.marketplaceProvideProduct.formatProduct()

        if (formattedProduct.variations.length === 0) {
          return notifier.warning('Nenhuma variação foi selecionada!', 'Selecione as variações')
        }

        this.update(formattedProduct)
          .then(() => {
            this.loading = true
            this.clearProductsMarketplace()
              .then(this.resetProduct)
          })
      },
      async savePrice () {
        this.clearErrors()

        const product = this.$refs.marketplaceProvideProduct.formatProduct()

        try {
          await api.mercadoLivre.updateProductPrice(product)
          notifier.success('Preços do anúncio salvos com sucesso!', 'Salvo')
        } catch (e) {
          notifier.error('Falha ao alterar os preços, tente novamente mais tarde.', 'Não alterado')
          await this.resetProduct()
        }
      },
      redirectToProductsSentReport () {
        this.$router.push(`/marketplace/mercadoLivre/announcements`)
      },
      async changeStatus () {
        this.loading = true

        let promise = null

        if (this.product.status === 'paused') {
          promise = this.activate(this.product)
        } else {
          promise = this.pause(this.product)
        }

        try {
          await promise

          notifier.success('Solicitação de alteração de status enviada!', 'Salvo')
          this.$router.push(`/marketplace/mercadoLivre/announcements`)
        } catch (e) {
          notifier.error('Falha ao alterar o status, tente novamente mais tarde.', 'Não alterado')
        } finally {
          this.loading = false
        }
      }
    },
    computed: {
      ...mapGetters('productsMarketplace', [
        'getById'
      ]),
      ...mapGetters('module', [
        'getDefinitionModuleByIdentifierAndType'
      ]),
      ...mapGetters('errors', [
        'getError'
      ]),
      ...mapFields({
        editAnnouncementUpdating: 'editAnnouncement.updating'
      }),
      toModuleDefinition () {
        return this.getDefinitionModuleByIdentifierAndType('marketplace', 'mercadoLivre')
      },
      statusActionText () {
        return this.product.status === 'active' ? 'Pausar' : 'Ativar'
      },
      statusActionIcon () {
        return {
          'icon-play3': this.product.status === 'paused',
          'icon-pause': this.product.status === 'active'
        }
      },
      canShowStatusAction () {
        if (this.product.status === 'active') {
          return true
        }

        if (this.product.status === 'paused' && this.product.variations.some(variation => variation.stock.quantity > 0 && variation.code !== undefined)) {
          return true
        }

        return false
      }
    },
    components: {
      PageTitle,
      Loader,
      Panel,
      FormGroup,
      MarketplaceAnnouncementEditProduct,
      ButtonActions
    }
  }
</script>



// WEBPACK FOOTER //
// Edit.vue?50c7cbd0